﻿declare const rowGrid: any;

interface IRowGridOptions {
	resize: boolean;
	minMargin?: number;
	maxMargin?: number;
	itemSelector: string;
	firstItemClass: string;
}

namespace eXpress.core {

	export interface ISelectPhotosActions {
		readonly selectionChanged?: (totalPhotos: number, totalSelected: number, selectAll: boolean) => void;
		readonly albumRenamed?: (name: string) => void;
		readonly photoClicked?: (photoId: string, closeHandler: () => void) => void;
		readonly deleteClicked?: (photoId: string) => void;
		readonly onVisible?: () => void;
	}

	export interface ISelectPhotosOptions {
		readonly readOnly: boolean;
		sortOptions: IPhotoSortOptions;
		readonly thumbRenderHook?: ($thumb: JQuery, fileId: string) => JQuery;
		readonly allThumbsRenderedHook?: ($container: JQuery) => JQuery;
		readonly photoIdFilter?: (photoId: string | undefined) => boolean;
		readonly photoNameFilter?: (photoId: string | undefined) => boolean;
		readonly itemClass?: string;
		readonly itemNotSelectableClass?: string;
		readonly pageSize?: number;
		readonly scrollContainerEl?: HTMLElement;
		readonly scrollContainerHeight?: () => number;
	}

	export class SelectPhotos implements IPanel<void> {
		private readonly css = {
			item: {
				selected: "selected",
				notSelected: "not-selected", // overrides allSelected of the container element
				imageError: "no-image"
			},
			container: {
				allSelected: "all-selected",
				isActive: "active"
			}
		};

		public folder: eXpress.core.IPhotoFolder;
		private photoEls: { [id: string]: JQuery | undefined } = {};
		public photos: AnyPhoto[];
		private filteredPhotoIds: string[];

		/**
		 * Deprecated: the $container should be managed outside selectphotos, use $placeholder instead.
		 */
		private readonly $container: JQuery;
		private readonly $placeholder: JQuery;
		private readonly storageService: services.StorageService;
		private selectedPhotoIds: string[];
		private selectAllWasActive: boolean;

		private currentSelectedPhotoIndex: number | undefined;

		private page: number;
		private readonly pageSize: number;
		private pageToken: string;

		private readonly rowGridOptions: IRowGridOptions;
		private readonly minGridItemHeight: number;

		private readonly itemClass: string;
		private readonly itemNotSelectableClass: string;
		private readonly itemSelector: string;

		private readonly handlers?: ISelectPhotosActions;
		private readonly options: ISelectPhotosOptions;

		private scrollEventDisposable?: IDisposable;

		private selectAll: boolean;

		private $deferred: JQueryDeferred<void>;

		private eventsDisposable?: IDisposable;

		private blocker: boolean;

		constructor($container: JQuery,
			storageService: services.StorageService,
			options: ISelectPhotosOptions,
			handlers?: ISelectPhotosActions) {
			this.$container = $container;
			this.$placeholder = $container.hasClass("placeholder") ? $container : $container.find(".placeholder");
			this.storageService = storageService;

			this.itemClass = options.itemClass || "photo";
			this.itemNotSelectableClass = options.itemNotSelectableClass || "disabled";
			this.itemSelector = `.item.${this.itemClass}:not(.${this.itemNotSelectableClass})`;

			this.handlers = handlers;
			this.options = options;

			this.page = 0;
			this.pageSize = options.pageSize || 60;
			this.pageToken = '';

			// TODO: rowgrid option should be passed in constructor
			// below code contains itemSelector which will ignore notSelectableItems by default
			// calls to redrawGrid(itemSelector?: string) can redraw grid with other itemSelector
			this.rowGridOptions = { resize: true, minMargin: 10, maxMargin: 11, itemSelector: `.item:not(.${this.itemNotSelectableClass})`, firstItemClass: "first-item" };
			this.minGridItemHeight = 300;

			this.selectedPhotoIds = [];
			this.selectAllWasActive = false;
		}

		private init(): void {
			this.$placeholder.on("click", ".icon-selected", (e) => { this.selectPhotoClickEvent(e); });
			this.$placeholder.on("click", ".icon-delete", (e) => { this.deletePhotoClickEvent(e); return false; });
			this.$placeholder.on("click", this.itemSelector, (e) => { this.shiftSelect(e); });
			this.$placeholder.on("click", this.itemSelector, (e) => { this.photoClickEvent(e); });

			if (this.options.scrollContainerHeight && this.options.scrollContainerEl) {
				this.eventsDisposable = utils.compositeDisposable([
					html.addEventListener(window, "resize", this.setScrollContainerHeight.bind(this)),
					html.addEventListener(window, "orientationchange", this.setScrollContainerHeight.bind(this))
				]);
			}

			this.bindScrollEvent();
		}

		private deinit(): void {
			this.$placeholder.off();
			this.unbindScrollEvent();
			if (this.eventsDisposable) this.eventsDisposable.dispose();
		}

		private getPhotosAsync(combineResultWith?: AnyPhoto[]): JQueryPromise<AnyPhoto[]> {
			return this.folder.getPhotosAsync(this.page, this.pageSize, this.options.sortOptions.sortBy, this.options.sortOptions.sortOrder, this.pageToken)
				.then(photosInFolder => {

					const lastItem = photosInFolder[photosInFolder.length - 1];
					const lastItemPageToken = (lastItem as partners.PartnerPhoto)?.pageToken;
					if (lastItemPageToken) {
						this.pageToken = lastItemPageToken;
					}

					const newPhotosWithCombinedResults = [...(combineResultWith || []), ...(photosInFolder || [])];
					if (newPhotosWithCombinedResults.length < this.pageSize) return newPhotosWithCombinedResults;
					const numberOfFilteredPhotos = this.getFilteredPhotos(photosInFolder).length;
					if (numberOfFilteredPhotos < (this.pageSize - this.pageSize / 3) && !this.areAllPhotosLoaded(newPhotosWithCombinedResults.length)) {
						this.page++;
						return this.getPhotosAsync(newPhotosWithCombinedResults);
					}

					return newPhotosWithCombinedResults;
				}, () => {
					throw new Error(utils.formatString("albumphotos: could not get photos for folder: {0}, page: {1}, pagesize: {2}", this.folder.id ? this.folder.id : this.folder.code || "", this.page.toString(), this.pageSize.toString()));
				});
		}

		private addPhotos(photos?: AnyPhoto[]) {
			if (!this.photos)
				this.photos = [];

			photos = photos || [];

			this.photos = [...this.photos, ...photos];

			this.filteredPhotoIds = [...(this.filteredPhotoIds || []), ...this.getFilteredPhotos(photos).map(p => p.code)];
		}

		private renderPhoto(photo: eXpress.core.IPhoto, img: HTMLImageElement, update = false) {
			if (!photo || (this.photoEls[photo.code] !== undefined || update))
				return;

			let $template = $(`
				<div>
					<div class="actions">
						<span class="icon-selected" data-icon="&#xe014;" aria-hidden="true"></span>
						<span class="icon-delete" data-icon="&#xe00e;" aria-hidden="true"></span>
					</div>
				</div>`
			);

			if (this.options.readOnly)
				$template.find(".actions .icon-delete").remove();

			const classes = ["item", this.itemClass];
			if (img.className === "error")
				classes.push(this.css.item.imageError);
			if (this.selectAll || this.selectAllWasActive) {
				classes.push(this.css.item.selected);
				this.selectedPhotoIds.push(photo.code);

				if (this.folder.fileCount < 0)
					this.selectionChanged(this.selectedPhotoIds.length);
			}
			else {
				this.selectionChanged(this.selectedPhotoIds.length);
			}


			$template.addClass(classes.join(" "));

			if (img.className === "error")
				$template.addClass(this.css.item.imageError);

			$template.attr("data-photo-id", photo.code);

			const $img = $(img).clone();
			$template.append($img);

			this.UpdateImageSize(photo, $img[0]);

			// allow the caller to modify the template
			if (this.options.thumbRenderHook)
				$template = this.options.thumbRenderHook($template, photo.code);

			if (this.options.photoNameFilter && this.options.photoIdFilter) {
				$template.toggleClass(this.itemNotSelectableClass, !this.options.photoNameFilter(photo.code) || !this.options.photoIdFilter(photo.code));
			}

			if (update) {
				const $photo = this.photoEls[photo.code];
				if (!$photo) throw new Error('Cant update photo. It does not exist in dictionary.');

				$photo.replaceWith($template);
			}
			else {
				this.photoEls[photo.code] = $template.insertBefore(this.$placeholder.find(".clear"));
			}
		}

		public loadNextPageAsync(): JQueryPromise<IPhotoWithLoadedImage[]> {
			if (!this.areAllPhotosLoaded() || this.folder.fileCount === -1) {
				this.page++;
				return this.getPhotosAsync().then(nextPhotos => {
					const returnVal: IPhotoWithLoadedImage[] = [];

					if (this.page === 1 && nextPhotos.length === 0) {
						this.folder.fileCount = 0;
						return returnVal;
					}

					this.addPhotos(nextPhotos);

					nextPhotos.forEach(photo => {
						const image = new Image(photo.width, photo.height);
						image.src = photo.mediumUrl;

						this.renderPhoto(photo, image);

						returnVal.push({ photo, image });
					});

					if (this.options.allThumbsRenderedHook) {
						this.options.allThumbsRenderedHook(this.$placeholder);
					}

					return returnVal;
				});
			}
			return $.Deferred<IPhotoWithLoadedImage[]>().resolve([]).promise();
		}

		public openAsync(rowGridItemClass?: string): JQueryPromise<void> {
			// TODO: move to onVisible of ISelectPhotoActions so the caller can set css class active on container when needed.
			// SelectPhotos doesn't need to know the container.
			this.$container.addClass("active");
			if (this.handlers && this.handlers.onVisible) this.handlers.onVisible();

			rowGrid(this.$placeholder[0], rowGridItemClass ? { ...this.rowGridOptions, itemSelector: rowGridItemClass } : this.rowGridOptions);
			this.toggleAllPhotos(false);

			this.setScrollContainerHeight();

			this.init();

			return this.$deferred = $.Deferred<void>();
		}

		private showHideNoPhotosMessage(): void {
			$(".gallery-photos-container .no-item").toggle(!this.photos || this.photos.length === 0);
		}

		public getSelectedPhotoIds(): string[] {
			return this.selectedPhotoIds;
		}

		private GetDeselected(): { deselected: boolean, numberOfFilesSelected: number } {
			const deselected = this.folder.fileCount > 0  && (this.selectAllWasActive || (this.areAllPhotosLoaded() && this.selectedPhotoIds.length > (this.folder.fileCount / 2)));
			return {
				deselected: deselected,
				numberOfFilesSelected: deselected ? this.folder.fileCount - (this.photos.length - this.selectedPhotoIds.length) : this.selectedPhotoIds.length
			};
		}

		public getSelectedPhotosAsync(): JQueryPromise<AnyPhoto[]> {
			let result: AnyPhoto[] = [];

			const useDeselected = this.GetDeselected().deselected;
			if (this.folder instanceof core.partners.PartnerFolder) {
				if (this.areAllPhotosLoaded() && this.selectedPhotoIds.length === this.folder.fileCount) {
					result = this.getFilteredPhotos(this.photos) as partners.PartnerPhoto[];
				}
				else {
					const photoIds = useDeselected ? this.photos.filter(x => !this.selectedPhotoIds.includes(x.code)).map(x => x.code) : this.selectedPhotoIds;
					if (useDeselected) {
						this.$placeholder.find(`.${this.itemClass}.${this.css.item.notSelected}`).each((index, el) => {
							photoIds.push(el.dataset.photoId ?? "");
						});
					}
					return this.storageService.getPartnerPhotosAsync(context.pointOfSaleId, GetCookie("accid"), this.folder.storeType, this.folder.id, photoIds, this.selectAll, useDeselected)
						.then(res => res && res.map(r => r as AnyPhoto));
				}
			}
			else {
				if (this.areAllPhotosLoaded() && this.selectedPhotoIds.length === this.folder.fileCount) {
					result = this.getFilteredPhotos(this.photos) as Photo[];
				}
				else {
					const selectedFileIds = useDeselected ? this.photos.filter(x => !this.selectedPhotoIds.includes(x.code)).map(x => x.code) : this.selectedPhotoIds;
					return this.storageService.getPhotosAsync(context.pointOfSaleId, GetCookie("accid"), this.folder.id || "", selectedFileIds, useDeselected, this.selectAll);
				}
			}

			return $.Deferred<AnyPhoto[]>().resolve(result || []);
		}

		private getFilteredPhotos(photos: AnyPhoto[], extraFilter?: (photo: AnyPhoto) => boolean): AnyPhoto[] {

			if (photos.length === 0 || (!this.options.photoIdFilter && !extraFilter)) {
				return photos;
			} else {
				return photos
					.filter(p =>
						p.code
						&& (!this.options.photoIdFilter || this.options.photoIdFilter(p.code))
						&& (!extraFilter || extraFilter(p))
					);
			}
		}

		public createAsync(folder: IPhotoFolder, sortOptions?: IPhotoSortOptions): JQueryPromise<IPhotoFolder> {
			this.folder = folder;
			this.clear();
			this.page = 0;
			this.pageToken = '';
			this.options.sortOptions = sortOptions ? sortOptions : this.options.sortOptions;

			// we need the file count for the current folder before loading the requested page.
			this.folder.fileCount = folder.fileCount;
			this.selectionChanged(0);
			return this.loadNextPageAsync().then(() => {
				this.showHideNoPhotosMessage();
				return this.folder;
			});
		}

		private selectionChanged(numberOfSelectedPhotos: number) {
			if (this.handlers && this.handlers.selectionChanged) {
				let fileCount = this.folder.fileCount;
				if (fileCount <= 0) {
					fileCount = this.filteredPhotoIds.length;
				}
				this.handlers.selectionChanged(fileCount, numberOfSelectedPhotos, this.filteredPhotoIds && numberOfSelectedPhotos >= this.filteredPhotoIds.length);
			}
		}

		public clear() {
			this.$placeholder.find(".item." + this.itemClass).remove();
			this.selectAll = false;
			this.$placeholder.removeClass(this.css.container.allSelected);
			this.photos = [];
			this.photoEls = {};
			this.selectedPhotoIds = [];
			this.filteredPhotoIds = [];
			this.selectionChanged(0);

			// reset scroll position
			if (this.options.scrollContainerEl) this.options.scrollContainerEl.scrollTop = 0;

			this.currentSelectedPhotoIndex = undefined;
		}

		private setScrollContainerHeight() {
			if (!this.options.scrollContainerEl || !this.options.scrollContainerHeight) return;
			this.options.scrollContainerEl.style.height = this.options.scrollContainerHeight() + "px";
		}

		private shiftSelect(event: JQuery.ClickEvent): void {
			if (event.shiftKey) {
				// stop other events when pressing shift while clicking
				event.stopImmediatePropagation();

				// itemSelector will skip non selectable photos
				const $photoItems = this.$placeholder.find(this.itemSelector);

				const targetIndex = $photoItems.index(event.currentTarget);

				const allIds = $.map<HTMLElement, string>($photoItems.toArray(), p => p!.getAttribute("data-photo-id")!);

				if (this.selectedPhotoIds.includes(allIds[targetIndex])) {
					/**
					 * if the target photo is selected: shift select will UNSELECT all the photos below the
					 * target photo that are part of the sequence of selected photos that the target belongs to
					 */
					let indexOfBottomSelectedSequence = targetIndex;
					for (let i = targetIndex + 1; i < allIds.length; i++) {
						const isSelected = this.selectedPhotoIds.includes(allIds[i]);
						if (!isSelected) break;
						indexOfBottomSelectedSequence = i;
					}
					const $unselected = $photoItems.slice(targetIndex + 1, indexOfBottomSelectedSequence + 1);
					const shiftUnselectedIds = $.map<HTMLElement, string>($unselected.toArray(), p => p!.getAttribute("data-photo-id")!);
					this.selectedPhotoIds = this.selectedPhotoIds.filter(item => !shiftUnselectedIds.includes(item));
					$unselected.toggleClass(this.css.item.selected, false);
					$unselected.toggleClass(this.css.item.notSelected, true);
				} else {
					/**
					 * if the target photo is NOT selected: shift select will SELECT
					 * all photos starting from the first selected photo above
					 * the target photo to the target photo
					 * (or from index 0 if no photo is selected above the target)
					 */
					let firstSelectedIndexAboveTarget = -1;
					for (let i = targetIndex - 1; i >= 0; i--) {
						const isSelected = this.selectedPhotoIds.includes(allIds[i]);
						if (isSelected) {
							firstSelectedIndexAboveTarget = i;
							break;
						}
					}
					const $selected = $photoItems.slice(firstSelectedIndexAboveTarget + 1, targetIndex + 1);
					const shiftSelectedIds = $.map<HTMLElement, string>($selected.toArray(), p => p!.getAttribute("data-photo-id")!);
					this.selectedPhotoIds = this.selectedPhotoIds.concat(shiftSelectedIds);
					$selected.toggleClass(this.css.item.notSelected, false);
					$selected.toggleClass(this.css.item.selected, true);
				}

				this.selectionChanged(this.selectedPhotoIds.length);

				// overwritten the default selection
				this.updateSelectAllProperties(false);
			}
		}

		// event handlers
		public closeHandler = () => {
			this.blocker = false;
		};

		private photoClickEvent(event: JQuery.ClickEvent): void {
			const $item = $(event.currentTarget as HTMLElement);
			const $items = this.$placeholder.find(this.itemSelector);

			if (this.handlers && this.handlers.photoClicked && this.selectedPhotoIds.length < 1) {
				if (this.blocker)
					return;
				this.blocker = true;

				this.handlers.photoClicked($item.attr("data-photo-id")!, this.closeHandler);
			}
			else {
				this.currentSelectedPhotoIndex = $items.index($item);
				this.toggleSinglePhoto(!this.isItemSelected($item), $item);
			}
		}

		private isItemSelected($item: JQuery) {
			const { container: { allSelected }, item: { selected, notSelected } } = this.css;
			return $item.hasClass(selected) || (this.$placeholder.hasClass(allSelected) && !$item.hasClass(notSelected));
		}

		private selectPhotoClickEvent(event: JQuery.ClickEvent): void {

			const $item = $(event.currentTarget).parents(".item");
			const $items = this.$placeholder.find(this.itemSelector);

			if (!event.shiftKey) {
				event.stopImmediatePropagation();
				this.currentSelectedPhotoIndex = $items.index($item);
				this.toggleSinglePhoto(!this.isItemSelected($item), $item);
			}

		}

		private deletePhotoClickEvent(event: JQuery.ClickEvent): void {
			const $item = $(event.currentTarget).parents(".item");
			if (this.handlers && this.handlers.deleteClicked)
				this.handlers.deleteClicked($item.attr("data-photo-id")!);
		}

		private bindScrollEvent() {
			this.scrollEventDisposable = html.addEventListener(this.options.scrollContainerEl || window, 'scroll', e => this.scrollEvent(e));
		}

		private unbindScrollEvent() {
			if (this.scrollEventDisposable) this.scrollEventDisposable.dispose();
		}

		private scrollEvent(event: UIEvent): void {
			if (this.areAllPhotosLoaded())
				return;

			const shouldLoadMore = () => {
				if (!this.options.scrollContainerEl) {
					const html = document.documentElement!;
					const containerHeight = this.$container.height()!;
					const body = document.body;
					const documentHeight = Math.max(body.scrollHeight, body.offsetHeight,
						html.clientHeight, html.scrollHeight, html.offsetHeight);

					const scrollTop = window.pageYOffset || html.scrollTop;
					const containerOffset = this.$container[0].getBoundingClientRect().top + html.scrollTop;
					const margin = documentHeight - (containerHeight + containerOffset);
					return scrollTop + $(window).height()! >= documentHeight - (margin + 450);
				} else {
					return this.options.scrollContainerEl.scrollTop + this.options.scrollContainerEl.offsetHeight + 250 > this.options.scrollContainerEl.scrollHeight;
				}
			};

			if (shouldLoadMore()) {
				if (this.unbindScrollEvent) this.unbindScrollEvent();
				core.showLoadingDialogAsync(this.loadNextPageAsync()
					.then(() => {
						this.appendedToGrid();
						this.bindScrollEvent();
					}, () => {
						this.bindScrollEvent();
					})
				);
			}
		}

		// update data and ui
		private updateSelectAllProperties(shouldSelect: boolean) {
			this.selectAll = shouldSelect;
			this.selectAllWasActive = !shouldSelect
				? false
				: this.selectAllWasActive || this.folder.fileCount > 0;

			this.$placeholder.toggleClass(this.css.container.allSelected, shouldSelect);
		}

		public toggleAllPhotos(shouldSelect: boolean): void {
			if (!this.filteredPhotoIds)
				return;

			// reset multi select (using shift key)
			this.currentSelectedPhotoIndex = undefined;

			this.updateSelectAllProperties(shouldSelect);

			if (shouldSelect) {
				// remove all not-selected classes
				this.$placeholder.find(`.${this.itemClass}`).removeClass(this.css.item.notSelected).addClass(this.css.item.selected);

				if (this.folder.fileCount > 0) {
					this.selectedPhotoIds = [...this.filteredPhotoIds]; // use a clone!!!

					this.selectionChanged(this.folder.fileCount);
				}
				else {
					this.selectedPhotoIds = [...this.filteredPhotoIds]; // use a clone!!!
					this.selectionChanged(this.selectedPhotoIds.length);
				}
			}
			else {
				this.$placeholder.find(`.${this.itemClass}`).removeClass(this.css.item.selected).addClass(this.css.item.notSelected);
				this.selectedPhotoIds = [];
				this.selectionChanged(0);
			}
		}

		private toggleSinglePhoto(shouldSelect: boolean, $item: JQuery) {
			const { container: { allSelected }, item: { selected, notSelected } } = this.css;
			$item.toggleClass(selected, shouldSelect);

			if (this.$placeholder.hasClass(allSelected)) {
				$item.toggleClass(notSelected, !shouldSelect);
			}


			if (shouldSelect) {
				this.selectedPhotoIds.push($item.attr("data-photo-id")!);
			}
			else {
				//remove the 'all-selected' class on container
				this.$placeholder.removeClass(this.css.container.allSelected);
				this.selectAll = false;
				this.selectedPhotoIds = eXpress.utils.removeItem(this.selectedPhotoIds, (id) => id === $item.attr("data-photo-id"));
				this.currentSelectedPhotoIndex = undefined;
			}

			const { numberOfFilesSelected } = this.GetDeselected();
			this.selectionChanged(numberOfFilesSelected);
		}

		public appendedToGrid(): void {
			rowGrid(this.$placeholder[0], "appended");
		}

		public redrawGrid(itemSelector?: string): void {
			const options = itemSelector ? { ...this.rowGridOptions, itemSelector } : this.rowGridOptions;
			rowGrid(this.$placeholder[0], options);
		}

		public updatePhoto(photo: Photo) {
			// update data
			this.photos[utils.findIndex(this.photos, (p: Photo) => p.code === photo.code)] = photo;

			// update image and grid
			eXpress.html.loadImageAsync(photo.mediumUrl)
				.then(image => image && this.updatePhotoUpdateUi(photo, image), failedImage => this.updatePhotoUpdateUi(photo, failedImage));
		}

		private updatePhotoUpdateUi(photo: core.IPhoto, img: HTMLImageElement) {
			// set image in grid
			const imageToUpdate = document.querySelector<HTMLDivElement>(`[data-photo-id="${photo.code}"]`);
			if (imageToUpdate) {
				const imgInDiv = imageToUpdate.getElementsByTagName('img')[0];
				imgInDiv.src = img.src;
				this.UpdateImageSize(photo, imgInDiv);
			}

			// render ui
			this.renderPhoto(photo, img, true);
			this.redrawGrid();
		}

		private UpdateImageSize(photo: eXpress.core.IPhoto, img: HTMLImageElement) {
			let width;
			let height;

			// remove attributes
			img.removeAttribute('width');
			img.removeAttribute('height');

			if (!photo.rotate90 || photo.rotate90 && (photo.rotate90 % 360 === 0 || photo.rotate90 % 360 === 180)) {
				// landscape
				width = photo.width;
				height = photo.height;
			} else {
				// portrait
				height = photo.width;
				width = photo.height;
			}

			// calucate ratio
			const ratio = width / height;
			const newWidth = ratio * this.minGridItemHeight;

			// add attributes
			img.setAttribute("height", this.minGridItemHeight.toString());
			img.setAttribute("width", newWidth.toString());
		}

		public removePhotos(ids: string[]) {
			if (!this.photos)
				return;

			this.photos = this.photos.filter(p => p.fileId && ids.indexOf(p.fileId) < 0);

			ids.forEach(id => {
				const $photo = this.photoEls[id];
				if ($photo) $photo.remove(); // remove the photo from the DOM
				this.photoEls[id] = undefined; // remove the entry from the domDictionary
			});

			// update folder if preview image is deleted
			const isPreviewImageDeletedOfAlbum = this.photos.filter(x => x.mediumUrl === this.folder.mediumUrl).length <= 0 || this.photos.length <= 0;
			if (isPreviewImageDeletedOfAlbum)
				this.updateFolderProperties();

			this.selectedPhotoIds = this.selectedPhotoIds.filter(id => ids.indexOf(id) < 0);
			this.filteredPhotoIds = this.getFilteredPhotos(this.photos).map(p => p.code);

			this.folder.fileCount -= ids.length;

			this.selectionChanged(0);
			this.redrawGrid();
		}

		public removeAllPhotos() {
			if (!this.photos)
				return;

			this.folder.fileCount = 0;

			this.selectionChanged(0);
			this.clear();
			this.updateFolderProperties();
		}

		private updateFolderProperties(): void {
			// set medium and thumb url for folder
			if (this.photos && this.photos.length > 0) {
				// for cover photo of the folder, take the first uploaded image
				const sortedPhotos = this.photos.sort((p1, p2) => {
					if (p1.dateUploaded === p2.dateUploaded)
						return 0;

					// make sure photos with undefined date uploaded are moved to the end
					if (!p1.dateUploaded || p1.dateUploaded > p2.dateUploaded!)
						return 1; // p2 before p1

					else return -1; // p1 before p2
				});
				this.folder.mediumUrl = sortedPhotos[0].mediumUrl;
				this.folder.thumbUrl = sortedPhotos[0].thumbUrl;
			}
			else {
				this.folder.mediumUrl = undefined;
				this.folder.thumbUrl = undefined;
			}
		}

		public close() {
			if (!this.folder.mediumUrl && this.photos.length > 0)
				this.updateFolderProperties();

			this.deinit();
			this.clear();

			const { isActive, allSelected } = this.css.container;
			this.$placeholder.removeClass(`${isActive} ${allSelected}`);

			this.$deferred.resolve();
		}

		public areAllPhotosLoaded(margin = 0): boolean {
			return this.pageToken == '__NoMorePages__';
		}
	}
}
